import React, { useCallback, useEffect, useState } from "react";
import { useAuth, hasAuthParams } from "react-oidc-context";
import { AUTH_MODES, logOutUser, userIsAuthenticated, userWasLoggedOutOrExpired, needsToBeRedirected, validateComponentUsage } from "../../utils/ssoHelpers";
export { AUTH_MODES };
const addPropAndRenderChildren = (children, prop) => {
  return /*#__PURE__*/React.cloneElement(children, prop);
};
export const SovosSSOCallback = props => {
  const [loginAttempt, setAttempt] = useState(false);
  const [accountConfirmed, setAccountConfirmed] = useState(false);
  let {
    auth
  } = props;
  const {
    removeUser,
    isLoading,
    isAuthenticated,
    user
  } = auth;
  const {
    login,
    validateAccountUpgrade,
    onUserAuthenticated,
    onUserAuthenticationFailed,
    UnauthorizedComponent,
    LoadingComponent,
    userRBAC
  } = props;
  const loginOnAuth = useCallback(() => {
    if (!loginAttempt && !isLoading && isAuthenticated && !userRBAC) {
      login(user, setAttempt);
    }
  }, [userRBAC, login, removeUser, isLoading, isAuthenticated, user, loginAttempt]);
  useEffect(() => {
    loginOnAuth();
    return () => {};
  }, [loginOnAuth]);
  if (isLoading) {
    return /*#__PURE__*/React.createElement(LoadingComponent, props);
  }
  if (!isLoading && isAuthenticated && userRBAC) {
    if (userRBAC && accountConfirmed) {
      return onUserAuthenticated(user, userRBAC);
    }
    if (!userRBAC.hasUpgradedAccount) {
      validateAccountUpgrade().then(accountUpgraded => setAccountConfirmed(accountUpgraded));
    } else {
      return onUserAuthenticated(user, userRBAC);
    }
  }
  if (!isLoading && !isAuthenticated && !userRBAC) {
    removeUser();
    return onUserAuthenticationFailed();
  }
  if (loginAttempt && !isLoading && isAuthenticated && !userRBAC) {
    return /*#__PURE__*/React.createElement(UnauthorizedComponent, props);
  }
  return /*#__PURE__*/React.createElement(LoadingComponent, props);
};
const SovosSSOComponent = props => {
  validateComponentUsage({
    ...props
  });
  let {
    onUserAuthenticated
  } = props;
  const {
    mode = "SSO",
    userInfo,
    checkValidSovosIdentity,
    legacyLoginAction,
    onUserLogOut,
    LoadingComponent,
    ErrorComponent,
    theme = null,
    children,
    auth,
    hasAuthParams
  } = props;
  if (!onUserAuthenticated) {
    onUserAuthenticated = user => {
      return auth.signinRedirect(user?.email ? {
        login_hint: user.email
      } : {});
    };
  }
  const [legacyLogin, setLegacyLogin] = useState(mode !== AUTH_MODES.SSO);
  const [isSovosIdentity, setAccountSovosIdentity] = useState(mode === AUTH_MODES.SSO);
  const handleSubmit = useCallback((username, password) => {
    if (username && username.trim().toLowerCase() !== "null") {
      window.localStorage.setItem("loginHint", username);
    }
    if (!isSovosIdentity && password) {
      setLegacyLogin(true);
      legacyLoginAction(username, password);
    } else {
      checkValidSovosIdentity(username).then(valid => {
        if (valid && auth) {
          setLegacyLogin(false);
          const queryParams = {
            login_hint: username
          };
          if (theme) {
            queryParams.extraQueryParams = {
              theme_key: theme
            };
          }
          auth.signinRedirect(queryParams);
        } else {
          setLegacyLogin(true);
          setAccountSovosIdentity(false);
        }
      }).catch(() => {
        setLegacyLogin(true);
        setAccountSovosIdentity(false);
      });
    }
  }, [auth, checkValidSovosIdentity, theme]);
  if (mode === AUTH_MODES.LEGACY) {
    return children;
  } else {
    useEffect(() => {
      if (needsToBeRedirected(hasAuthParams(), auth, userInfo, mode, legacyLogin)) {
        const queryParams = {};
        if (userInfo) {
          queryParams.login_hint = userInfo.loginHint;
        }
        if (theme) {
          queryParams.extraQueryParams = {
            theme_key: theme
          };
        }
        auth.signinRedirect(queryParams);
      }
      if (userWasLoggedOutOrExpired(hasAuthParams(), auth, userInfo, legacyLogin, onUserLogOut)) {
        if (!legacyLogin) {
          setLegacyLogin(true);
        } else {
          onUserLogOut(auth);
        }
      }
    }, [auth, userInfo, legacyLogin]);
    if (userIsAuthenticated(auth, onUserAuthenticated)) {
      onUserAuthenticated(auth.user);
    }
    if (logOutUser(auth)) {
      auth.signoutRedirect();
      return /*#__PURE__*/React.createElement(LoadingComponent, props);
    }
    if (!auth.isLoading && auth.error) {
      return /*#__PURE__*/React.createElement(ErrorComponent, props) ?? /*#__PURE__*/React.createElement("div", null, "Oops something went wrong: $", auth.error);
    }
    return mode === AUTH_MODES.HYBRID ? addPropAndRenderChildren(children, {
      onLoginSubmit: handleSubmit,
      userInfo,
      mode: props.mode
    }) : /*#__PURE__*/React.createElement(LoadingComponent, props);
  }
};
export default SovosSSOComponent;